import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HitApi } from '../../Api/ApiHit';
import { searchCashTransaction } from '../../constant/Constant';
import { setPagination } from '../../redux/Actions/TableAction';
import toast from 'react-hot-toast';
import MongoosePagination from '../../components/Pagination/MongoosePagination';
import DateSearch from '../../components/DateSearch/DateSearch';
import { CalendarRange } from 'lucide-react';

function TransactionHistory({ selectedUnit, refreshTrigger = 0, onFiltersChange }) {
  const dispatch = useDispatch();
  const TableDataReducer = useSelector((state) => state.TableReducer);

  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    transactionType: 'all',
    category: 'all',
    dateRange: '7days'
  });
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Helper function to convert date string to timestamp
  const convertDateToTimestamp = (dateString, isStartOfDay = true) => {
    const [year, month, day] = dateString.split('-').map(Number);
    if (isStartOfDay) {
      return new Date(year, month - 1, day, 0, 0, 0, 0).getTime();
    } else {
      return new Date(year, month - 1, day, 23, 59, 59, 999).getTime();
    }
  };

  // Fetch transactions from API
  const fetchTransactions = useCallback(async () => {
    if (!selectedUnit?._id) {
      console.warn('No unit selected, cannot fetch transactions');
      return;
    }

    setIsLoading(true);
    try {
      // Build search criteria
      const searchCriteria = {
        unitId: selectedUnit._id
      };

      // Add transaction type filter
      if (filters.transactionType !== 'all') {
        searchCriteria.transactionType = filters.transactionType;
      }

      // Add category filter
      if (filters.category !== 'all') {
        searchCriteria.category = filters.category;
      }

      // Add date range filter
      if (filters.dateRange === 'custom') {
        // Handle custom date range
        if (customStartDate && customEndDate) {
          searchCriteria.createdAt = {
            $gte: convertDateToTimestamp(customStartDate, true),
            $lte: convertDateToTimestamp(customEndDate, false)
          };
        }
      } else if (filters.dateRange !== 'all') {
        const now = new Date();
        let startDate;

        switch (filters.dateRange) {
          case '1day':
            startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            break;
          case '7days':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case '30days':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          default:
            startDate = null;
        }

        if (startDate) {
          searchCriteria.createdAt = {
            $gte: startDate.getTime()
          };
        }
      }

      const requestPayload = {
        page: TableDataReducer.pagination.page,
        limit: TableDataReducer.pagination.limit,
        search: searchCriteria,
        sort: { createdAt: -1 } // Most recent first
      };

      console.log('Fetching transactions with payload:', requestPayload);

      // Notify parent component about active filters for export
      if (onFiltersChange) {
        onFiltersChange(searchCriteria);
      }

      const response = await HitApi(requestPayload, searchCashTransaction);

      if (response?.statusCode === 200 && response?.data) {
        const { docs, totalDocs } = response.data;

        setTransactions(docs || []);

        // Update pagination in Redux
        const paginationData = {
          total: totalDocs || 0,
          page: TableDataReducer.pagination.page,
          limit: TableDataReducer.pagination.limit,
        };
        dispatch(setPagination(paginationData));

        console.log(`Loaded ${docs?.length || 0} transactions`);
      } else {
        console.log('No transactions found');
        setTransactions([]);
        dispatch(setPagination({
          total: 0,
          page: TableDataReducer.pagination.page,
          limit: TableDataReducer.pagination.limit,
        }));
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to fetch transaction history');
      setTransactions([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedUnit, filters, customStartDate, customEndDate, TableDataReducer.pagination.page, TableDataReducer.pagination.limit, dispatch]);

  // Load transactions when component mounts or dependencies change
  useEffect(() => {
    if (selectedUnit) {
      fetchTransactions();
    }
  }, [selectedUnit, fetchTransactions, refreshTrigger]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    // Reset to first page when filters change
    dispatch(setPagination({
      ...TableDataReducer.pagination,
      page: 1
    }));
  };

  // Handle custom date range search
  const handleDateRangeSearch = (startDate, endDate) => {
    console.log('Date range search:', startDate, 'to', endDate);
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
  };

  // Handle clear custom date range
  const handleClearDateRange = () => {
    console.log('Clearing date range');
    setCustomStartDate('');
    setCustomEndDate('');
    setFilters(prev => ({
      ...prev,
      dateRange: '7days'
    }));
  };

  // Format transaction amount with proper sign
  const formatAmount = (transaction) => {
    const sign = transaction.transactionType === 'in' ? '+' : '-';
    return `${sign}₹${transaction.totalAmount?.toLocaleString() || 0}`;
  };

  // Get transaction type badge color
  const getTransactionTypeBadge = (type) => {
    const classes = {
      'in': 'bg-green-100 text-green-800',
      'out': 'bg-red-100 text-red-800'
    };
    return classes[type] || 'bg-gray-100 text-gray-800';
  };

  // Get category badge color
  const getCategoryBadge = (category) => {
    const classes = {
      'sale': 'bg-blue-100 text-blue-800',
      'expense': 'bg-red-100 text-red-800',
      'deposit': 'bg-green-100 text-green-800',
      'withdrawal': 'bg-orange-100 text-orange-800',
      'transfer': 'bg-purple-100 text-purple-800',
      'refund': 'bg-yellow-100 text-yellow-800',
      'other': 'bg-gray-100 text-gray-800'
    };
    return classes[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Transaction History</h2>
          <p className="text-sm text-gray-500">
            {TableDataReducer.pagination.total > 0 ?
              `Showing ${transactions.length} of ${TableDataReducer.pagination.total} transactions` :
              'No transactions found'
            }
          </p>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Transaction Type Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Transaction Type</label>
            <select
              value={filters.transactionType}
              onChange={(e) => handleFilterChange('transactionType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-400 focus:border-blue-400 text-sm"
            >
              <option value="all">All Types</option>
              <option value="in">Cash In</option>
              <option value="out">Cash Out</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-400 focus:border-blue-400 text-sm"
            >
              <option value="all">All Categories</option>
              <option value="sale">Sale</option>
              <option value="expense">Expense</option>
              <option value="deposit">Deposit</option>
              <option value="withdrawal">Withdrawal</option>
              <option value="transfer">Transfer</option>
              <option value="refund">Refund</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Date Range Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date Range</label>
            <select
              value={filters.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-400 focus:border-blue-400 text-sm"
            >
              <option value="1day">Last 24 Hours</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Date Range</option>
            </select>
          </div>
        </div>

        {/* Custom Date Range Selector */}
        {filters.dateRange === 'custom' && (
          <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <CalendarRange className="h-5 w-5 text-blue-600" />
                <h3 className="text-sm font-medium text-gray-700">Select Custom Date Range</h3>
              </div>
              <DateSearch
                onSearch={handleDateRangeSearch}
                onClear={handleClearDateRange}
              />
            </div>
          </div>
        )}
      </div>

      {/* Transaction List */}
      <div className="p-6">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Loading transactions...</p>
            </div>
          </div>
        ) : transactions.length > 0 ? (
          <div className="space-y-4">
            {transactions.map((transaction, index) => (
              <div key={transaction._id || index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTransactionTypeBadge(transaction.transactionType)}`}>
                        {transaction.transactionType === 'in' ? 'Cash In' : 'Cash Out'}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryBadge(transaction.category)}`}>
                        {transaction.category?.charAt(0).toUpperCase() + transaction.category?.slice(1)}
                      </span>
                      <span className="text-xs text-gray-500">
                        {transaction.createdAt ? new Date(transaction.createdAt).toLocaleString() : 'Unknown Date'}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">
                      {transaction.description || 'No description'}
                    </p>

                    {/* Account To Information */}
                    {transaction.inAccountTo && (
                      <div className="mb-2">
                        <span className="text-xs text-gray-500">Account To: </span>
                        <span className="text-xs font-medium text-gray-700">
                          {transaction.inAccountTo.name}
                        </span>
                        {transaction.recordedBy && (
                          <>
                            <span className="text-xs text-gray-500"> • Recorded by: </span>
                            <span className="text-xs font-medium text-gray-700">
                              {transaction.recordedBy.name}
                            </span>
                          </>
                        )}
                      </div>
                    )}

                    {/* Denomination Breakdown */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs text-gray-600">
                      {transaction.note500 > 0 && (
                        <span>₹500: {transaction.note500}</span>
                      )}
                      {transaction.note200 > 0 && (
                        <span>₹200: {transaction.note200}</span>
                      )}
                      {transaction.note100 > 0 && (
                        <span>₹100: {transaction.note100}</span>
                      )}
                      {transaction.note50 > 0 && (
                        <span>₹50: {transaction.note50}</span>
                      )}
                      {transaction.note10 > 0 && (
                        <span>₹10: {transaction.note10}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right ml-4">
                    <div className={`text-lg font-semibold ${
                      transaction.transactionType === 'in' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatAmount(transaction)}
                    </div>
                    <div className="text-xs text-gray-500">
                      Balance: ₹{transaction.newBalance?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">No Transactions Found</h3>
            <p className="text-xs text-gray-500">
              No cash transactions match your current filters.
            </p>
          </div>
        )}

        {/* Pagination */}
        {TableDataReducer.pagination.total > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <MongoosePagination />
          </div>
        )}
      </div>
    </div>
  );
}

export default TransactionHistory;